import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaBolt,
  FaBullhorn,
  FaCalendarCheck,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaComments,
  FaCompass,
  FaCopy,
  FaGift,
  FaHandshake,
  FaHistory,
  FaInfoCircle,
  FaLock,
  FaMagic,
  FaPaperPlane,
  FaRobot,
  FaRoute,
  FaStore,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import './ForfyCommerceCockpit.css';
import ForfyCommerceOfferAssistant from './ForfyCommerceOfferAssistant';

export interface ForfyCommerceContext {
  source?: string;
  businessId?: string | null;
  businessName?: string | null;
  businessPlan?: string | null;
  activeTab?: string | null;
  activeNav?: string | null;
  userRole?: string | null;
  merchantContext?: Record<string, any> | null;
}

interface ForfyCommerceCockpitProps {
  context?: ForfyCommerceContext | null;
  onClose: () => void;
  onOpenTab: (tab: string) => void;
  onSeedPrompt: (prompt: string) => void;
}

interface CommerceBrief {
  objective: string;
  targetClient: string;
  channel: string;
  tone: string;
  extraContext: string;
}

interface CommerceAction {
  id: string;
  title: string;
  eyebrow: string;
  description: string;
  tab: string;
  icon: React.ReactNode;
  output: string;
  buildPrompt: (context: ResolvedCommerceContext, brief: CommerceBrief) => string;
}

interface CommercePlaybook {
  id: string;
  title: string;
  category: string;
  description: string;
  actionId: string;
  tab: string;
  objective: string;
  targetClient: string;
  channel: string;
  tone: string;
  notes: string;
  checklist: string[];
}

interface ResolvedCommerceContext {
  businessName: string;
  activeTab: string;
  activeTabLabel: string;
  userRole: string;
  businessPlan: string;
}

interface ActionHistoryEntry {
  id: string;
  actionId: string;
  title: string;
  status: 'préparé' | 'copié' | 'envoyé';
  createdAt: string;
}

interface GeneratedActionKit {
  title: string;
  summary: string;
  checklist: string[];
  teamMessage: string;
  nextStep: string;
}

interface GeneratedCampaignPack {
  title: string;
  audience: string;
  promise: string;
  channels: Array<{ label: string; content: string }>;
  contentAngles: string[];
  manualChecks: string[];
}

interface ActionPlanItem {
  id: string;
  actionId: string;
  title: string;
  priority: 'haute' | 'moyenne' | 'normale';
  status: 'à faire' | 'en cours' | 'terminé';
  createdAt: string;
  summary: string;
  nextStep: string;
}

const quickTabs = [
  { id: 'offersHub', label: 'Offres' },
  { id: 'reserveHub', label: 'Reserve' },
  { id: 'calendar', label: 'Calendrier' },
  { id: 'messages', label: 'CRM' },
  { id: 'performance', label: 'Performance' },
  { id: 'posts', label: 'Marketing' },
  { id: 'combos', label: 'Bundles' },
  { id: 'revenues', label: 'Revenus' },
];

const tabLabels: Record<string, string> = {
  overview: 'Vue d’ensemble',
  offersHub: 'Offres & expériences',
  reserveHub: 'Réservations & Reserve',
  calendar: 'Calendrier marchand',
  messages: 'CRM clients',
  performance: 'Performance',
  posts: 'Marketing / Pulse',
  marketingHub: 'Marketing / Pulse',
  combos: 'Collaborations & Bundles',
  revenues: 'Revenus',
  hotel: 'Offre hébergement',
  reserve: 'Portail Reserve',
  booking: 'Moteur de réservation',
  integrations: 'Widget & API Reserve',
  'virtual-tour': 'Visite virtuelle 360°',
  loyalty: 'Fidélité',
};

const objectiveOptions = [
  'Augmenter les réservations',
  'Créer une offre premium',
  'Remplir des disponibilités lentes',
  'Relancer des clients existants',
  'Améliorer une description',
  'Préparer une publication',
];

const channelOptions = [
  'Forfeo',
  'Facebook / Instagram',
  'TikTok',
  'LinkedIn',
  'Courriel',
  'SMS / Messenger',
];

const toneOptions = [
  'Premium et chaleureux',
  'Simple et direct',
  'Familial et accessible',
  'Urgence douce',
  'Corporate professionnel',
  'Touristique inspirant',
];

const defaultBrief: CommerceBrief = {
  objective: objectiveOptions[0],
  targetClient: 'Client local ou voyageur qui cherche une expérience prête à réserver',
  channel: channelOptions[0],
  tone: toneOptions[0],
  extraContext: '',
};

const financialBoundary = [
  'Ne modifie aucun paiement.',
  'Ne fixe aucun prix officiel.',
  'Ne promets aucun remboursement, rabais, commission, payout, taxe ou disponibilité non confirmée.',
  'Ne crée aucune référence Stripe et ne recalcule aucun revenu officiel.',
  'Giftshop demeure la source financière et transactionnelle.',
].join('\n- ');

const buildBriefBlock = (brief: CommerceBrief) => `BRIEF MARCHAND
- Objectif: ${brief.objective}
- Client cible: ${brief.targetClient || 'à préciser'}
- Canal prioritaire: ${brief.channel}
- Ton: ${brief.tone}
- Notes terrain: ${brief.extraContext || 'aucune note ajoutée'}`;

const buildContextHeader = (ctx: ResolvedCommerceContext, brief: CommerceBrief, mission: string) => `Agis comme Forfy Commerce, assistant marchand de Forfeo.

CONTEXTE
- Commerce: ${ctx.businessName}
- Onglet actif: ${ctx.activeTabLabel} (${ctx.activeTab})
- Rôle utilisateur: ${ctx.userRole}
- Plan connu: ${ctx.businessPlan}
- Mission: ${mission}

${buildBriefBlock(brief)}

GARDE-FOUS FINANCIERS OBLIGATOIRES
- ${financialBoundary}

RÈGLE DE SORTIE
Réponds en français, de façon pratique, terrain, courte et actionnable. Si une donnée manque, demande-la clairement au lieu d’inventer.`;

const buildPrompt = (ctx: ResolvedCommerceContext, brief: CommerceBrief, mission: string, expectedOutput: string) => `${buildContextHeader(ctx, brief, mission)}

FORMAT ATTENDU
${expectedOutput}`;


const commerceActions: CommerceAction[] = [
  {
    id: 'today',
    title: 'Plan d’action du jour',
    eyebrow: 'Priorités',
    description: 'Transformer le contexte du portail en prochaines actions marchandes concrètes.',
    tab: 'overview',
    icon: <FaBolt />,
    output: 'Résumé, priorités, vérifications et message prêt pour l’équipe.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Établir les priorités commerciales et opérationnelles du jour.', '1) Résumé en 3 lignes\n2) 5 actions prioritaires classées par impact\n3) Message prêt à envoyer à l’équipe\n4) Points à vérifier manuellement'),
  },
  {
    id: 'offer',
    title: 'Créer une offre premium',
    eyebrow: 'Offres',
    description: 'Structurer une expérience, un forfait, un hébergement ou un restaurant plus vendeur.',
    tab: 'offersHub',
    icon: <FaStore />,
    output: 'Titre, description, inclusions, exclusions, conditions, photos, angles marketing.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Créer ou améliorer une offre Forfeo Commerce sans prix inventé.', '1) Titre premium\n2) Description courte\n3) Description détaillée\n4) Inclusions\n5) Exclusions\n6) Conditions\n7) Photos ou médias à prévoir\n8) 3 angles marketing\n9) Questions à poser avant publication'),
  },
  {
    id: 'reserve',
    title: 'Audit Reserve assistif',
    eyebrow: 'Reserve',
    description: 'Identifier les disponibilités, politiques, ressources ou blocages à surveiller.',
    tab: 'reserveHub',
    icon: <FaCalendarCheck />,
    output: 'Lecture opérationnelle, risques, vérifications et prochaines actions.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Analyser Reserve en lecture assistive.', '1) Ce qui semble prêt\n2) Ce qui manque\n3) Risques opérationnels\n4) Vérifications à faire\n5) Prochaines actions avant ouverture aux clients'),
  },
  {
    id: 'marketing',
    title: 'Publication marketing',
    eyebrow: 'Pulse',
    description: 'Préparer un contenu pour Facebook, Instagram, TikTok ou LinkedIn.',
    tab: 'posts',
    icon: <FaBullhorn />,
    output: 'Post social, accroche vidéo, hashtags, appel à l’action, variation premium.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Préparer une publication marketing Forfeo.', '1) Version Facebook / Instagram\n2) Accroche TikTok réaliste\n3) Version LinkedIn courte\n4) 5 hashtags\n5) Appel à l’action\n6) Variante plus premium\n7) Mention des éléments à confirmer avant publication'),
  },
  {
    id: 'client',
    title: 'Réponse client',
    eyebrow: 'CRM',
    description: 'Rédiger une réponse claire, chaleureuse et professionnelle à un client.',
    tab: 'messages',
    icon: <FaComments />,
    output: 'Réponse courte, réponse détaillée, questions et prochaine étape.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Préparer une réponse client sans promesse non confirmée.', '1) Réponse courte\n2) Réponse plus complète\n3) Questions à poser au client\n4) Phrase de fermeture professionnelle\n5) Ce qu’il faut éviter de promettre'),
  },
  {
    id: 'bundle',
    title: 'Idées de bundles',
    eyebrow: 'Collaborations',
    description: 'Imaginer des partenariats locaux avec hôtel, resto, spa ou activité.',
    tab: 'combos',
    icon: <FaHandshake />,
    output: 'Partenaires cibles, valeur client, message d’approche et garde-fous.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Proposer des collaborations et bundles locaux.', 'Donne 5 idées. Pour chacune: partenaire cible, concept, valeur client, message d’approche, friction possible, garde-fou opérationnel.'),
  },
  {
    id: 'performance',
    title: 'Lecture performance',
    eyebrow: 'Coaching',
    description: 'Expliquer les tendances visibles sans recalculer les revenus officiels.',
    tab: 'performance',
    icon: <FaChartLine />,
    output: 'Ce qui va bien, risques, actions correctives et données à vérifier.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Lire la performance commerciale de manière assistive.', '1) Ce qui semble bien aller\n2) Ce qui mérite attention\n3) 3 actions correctives\n4) Données à vérifier dans Giftshop / Reserve avant décision\n5) Aucun recalcul financier officiel'),
  },
  {
    id: 'loyalty',
    title: 'Relance fidélité',
    eyebrow: 'Fidélité',
    description: 'Préparer une relance pour faire revenir les clients sans crédit inventé.',
    tab: 'loyalty',
    icon: <FaGift />,
    output: 'Message court, courriel premium, fréquence et segmentation simple.',
    buildPrompt: (ctx, brief) => buildPrompt(ctx, brief, 'Créer une relance fidélité non financière.', '1) Message SMS court\n2) Courriel premium\n3) Idée d’expérience à recommander\n4) Fréquence de relance\n5) Segments clients à prioriser\n6) Aucune promesse de crédit, remboursement ou rabais inventé'),
  },
];

const playbookSteps = [
  'Choisir une mission',
  'Préparer le brouillon',
  'Compléter le brief',
  'Valider manuellement',
];

const commercePlaybooks: CommercePlaybook[] = [
  {
    id: 'fill-slow-week',
    title: 'Remplir une semaine creuse',
    category: 'Réservations',
    description: 'Transformer une période lente en campagne simple avec offre, relance et visibilité locale.',
    actionId: 'reserve',
    tab: 'reserveHub',
    objective: 'Remplir des disponibilités lentes',
    targetClient: 'Familles, couples et clients locaux qui peuvent réserver rapidement',
    channel: 'Facebook / Instagram',
    tone: 'Urgence douce',
    notes: 'Mettre en avant les disponibilités à confirmer manuellement et éviter toute promesse de prix ou de rabais non approuvé.',
    checklist: ['Identifier les créneaux à remplir', 'Préparer un message court', 'Valider disponibilité et conditions', 'Diffuser après validation humaine'],
  },
  {
    id: 'premium-experience-launch',
    title: 'Lancer une offre premium',
    category: 'Offres',
    description: 'Structurer une expérience haut de gamme avec titre, inclusions, médias et angles de vente.',
    actionId: 'offer',
    tab: 'offersHub',
    objective: 'Créer une offre premium',
    targetClient: 'Voyageur ou client local qui recherche une expérience clé en main',
    channel: 'Forfeo',
    tone: 'Premium et chaleureux',
    notes: 'Construire la valeur perçue sans inventer de prix, taxes, disponibilité ou politique commerciale.',
    checklist: ['Clarifier la promesse client', 'Lister inclusions et exclusions', 'Prévoir photos ou vidéos', 'Préparer les questions avant publication'],
  },
  {
    id: 'seasonal-campaign',
    title: 'Campagne saisonnière',
    category: 'Marketing',
    description: 'Créer un pack de contenu saisonnier pour faire rayonner une expérience au bon moment.',
    actionId: 'marketing',
    tab: 'posts',
    objective: 'Préparer une publication',
    targetClient: 'Client sensible aux sorties saisonnières et aux expériences locales',
    channel: 'Facebook / Instagram',
    tone: 'Touristique inspirant',
    notes: 'Adapter le message à la saison sans publication automatique et avec validation humaine obligatoire.',
    checklist: ['Choisir l’angle saisonnier', 'Préparer 3 variantes de message', 'Valider médias et disponibilités', 'Envoyer vers Pulse plus tard seulement si autorisé'],
  },
  {
    id: 'winback-customers',
    title: 'Relancer anciens clients',
    category: 'CRM',
    description: 'Préparer une relance polie pour clients existants sans promettre de crédit ou rabais non approuvé.',
    actionId: 'client',
    tab: 'messages',
    objective: 'Relancer des clients existants',
    targetClient: 'Clients ayant déjà démontré un intérêt pour une expérience Forfeo',
    channel: 'Courriel',
    tone: 'Simple et direct',
    notes: 'Créer une relance humaine, utile et non agressive, avec prochaine étape claire.',
    checklist: ['Segmenter les clients à relancer', 'Préparer message court', 'Ajouter question ouverte', 'Valider avant envoi'],
  },
  {
    id: 'local-bundle',
    title: 'Créer un bundle local',
    category: 'Collaborations',
    description: 'Imaginer une collaboration hôtel, resto, spa ou activité avec valeur client claire.',
    actionId: 'bundle',
    tab: 'combos',
    objective: 'Créer une offre premium',
    targetClient: 'Couples, familles ou touristes qui veulent une expérience complète',
    channel: 'LinkedIn',
    tone: 'Corporate professionnel',
    notes: 'Proposer des partenariats sans engagement financier, prix, commission ou disponibilité non validée.',
    checklist: ['Identifier 3 partenaires', 'Formuler la valeur client', 'Préparer message d’approche', 'Lister les frictions à clarifier'],
  },
  {
    id: 'review-response',
    title: 'Répondre à un mauvais commentaire',
    category: 'Réputation',
    description: 'Préparer une réponse calme, professionnelle et orientée solution.',
    actionId: 'client',
    tab: 'messages',
    objective: 'Préparer une publication',
    targetClient: 'Client insatisfait ou prospect qui observe la réponse publique du commerce',
    channel: 'SMS / Messenger',
    tone: 'Simple et direct',
    notes: 'Ne jamais promettre remboursement, crédit ou geste commercial sans validation officielle.',
    checklist: ['Reconnaître le problème', 'Rester factuel', 'Inviter en privé', 'Valider tout geste avant promesse'],
  },
  {
    id: 'last-minute-boost',
    title: 'Promouvoir une offre dernière minute',
    category: 'Performance',
    description: 'Préparer une poussée rapide pour une expérience prête à confirmer.',
    actionId: 'performance',
    tab: 'performance',
    objective: 'Augmenter les réservations',
    targetClient: 'Client flexible qui peut réserver dans les prochains jours',
    channel: 'TikTok',
    tone: 'Urgence douce',
    notes: 'Mettre l’accent sur la découverte et la disponibilité à confirmer, sans rabais inventé.',
    checklist: ['Vérifier disponibilité', 'Préparer accroche courte', 'Créer message urgence douce', 'Suivre les résultats sans recalcul financier officiel'],
  },
];


const workspaceNav = [
  { id: 'forfy-home', label: 'Accueil', helper: 'Vue intelligente', icon: <FaStore /> },
  { id: 'forfy-missions', label: 'Missions', helper: 'Actions rapides', icon: <FaCompass /> },
  { id: 'forfy-playbooks', label: 'Playbooks', helper: 'Modèles IA', icon: <FaMagic /> },
  { id: 'forfy-brief', label: 'Brief marchand', helper: 'Contexte', icon: <FaClipboardList /> },
  { id: 'forfy-draft', label: 'Brouillon', helper: 'Prompt visible', icon: <FaCopy /> },
  { id: 'forfy-kit', label: 'Kit campagne', helper: 'Livrable', icon: <FaGift /> },
  { id: 'forfy-plan', label: 'Plan d’action', helper: 'Suivi local', icon: <FaClipboardList /> },
  { id: 'forfy-sprint', label: 'Sprint 7 jours', helper: 'Exécution', icon: <FaRoute /> },
  { id: 'forfy-campaign', label: 'Campagne', helper: 'Multicanal', icon: <FaBullhorn /> },
  { id: 'forfy-history', label: 'Historique', helper: 'Local', icon: <FaHistory /> },
];


function resolveContext(context?: ForfyCommerceContext | null): ResolvedCommerceContext {
  const activeTab = context?.activeTab || context?.activeNav || 'overview';

  return {
    businessName: context?.businessName || 'Commerce Forfeo',
    activeTab,
    activeTabLabel: tabLabels[activeTab] || activeTab,
    userRole: context?.userRole || 'commerce',
    businessPlan: context?.businessPlan || 'non précisé',
  };
}

function buildHistoryId() {
  return `forfy-commerce-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildHistoryStorageKey(ctx: ResolvedCommerceContext) {
  return `forfy-commerce-action-history:${ctx.businessName}:${ctx.userRole}`;
}

function buildBriefStorageKey(ctx: ResolvedCommerceContext) {
  return `forfy-commerce-smart-brief:${ctx.businessName}:${ctx.userRole}`;
}


function buildPlanStorageKey(ctx: ResolvedCommerceContext) {
  return `forfy-commerce-action-plan:${ctx.businessName}:${ctx.userRole}`;
}

function resolveActionPriority(action: CommerceAction): ActionPlanItem['priority'] {
  if (action.id === 'today' || action.id === 'reserve' || action.id === 'performance') return 'haute';
  if (action.id === 'offer' || action.id === 'marketing' || action.id === 'bundle') return 'moyenne';
  return 'normale';
}

function buildPlanItem(action: CommerceAction, kit: GeneratedActionKit): ActionPlanItem {
  return {
    id: buildHistoryId(),
    actionId: action.id,
    title: action.title,
    priority: resolveActionPriority(action),
    status: 'à faire',
    createdAt: new Date().toISOString(),
    summary: kit.summary,
    nextStep: kit.nextStep,
  };
}

function nextPlanStatus(status: ActionPlanItem['status']): ActionPlanItem['status'] {
  if (status === 'à faire') return 'en cours';
  if (status === 'en cours') return 'terminé';
  return 'à faire';
}

function planStatusSlug(status: ActionPlanItem['status']) {
  if (status === 'en cours') return 'running';
  if (status === 'terminé') return 'done';
  return 'todo';
}

function formatHistoryTime(value: string) {
  try {
    return new Intl.DateTimeFormat('fr-CA', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
}


function buildActionKit(action: CommerceAction, ctx: ResolvedCommerceContext, brief: CommerceBrief): GeneratedActionKit {
  const safeObjective = brief.objective || 'objectif à préciser';
  const safeTarget = brief.targetClient || 'client cible à préciser';
  const safeChannel = brief.channel || 'canal à préciser';
  const safeTone = brief.tone || 'ton à préciser';
  const safeNotes = brief.extraContext || 'aucune note terrain ajoutée';

  return {
    title: `Kit ${action.title}`,
    summary: `Pour ${ctx.businessName}, Forfy recommande de travailler la mission « ${action.title} » sur l’onglet ${ctx.activeTabLabel}, avec l’objectif « ${safeObjective} », pour ${safeTarget}, sur ${safeChannel}, dans un ton ${safeTone}.`,
    checklist: [
      `Valider les informations visibles dans ${ctx.activeTabLabel}.`,
      'Compléter les données manquantes avant publication ou réponse client.',
      `Adapter le message au canal ${safeChannel}.`,
      `Relire les notes terrain: ${safeNotes}.`,
      'Confirmer manuellement prix, disponibilités, paiements, remboursements et conditions avant toute décision.',
    ],
    teamMessage: `Équipe, priorité Forfeo: ${action.title}. Objectif: ${safeObjective}. Cible: ${safeTarget}. Canal: ${safeChannel}. Merci de valider les infos opérationnelles avant diffusion ou suivi client.`,
    nextStep: `Ouvrir ${ctx.activeTabLabel}, appliquer la checklist, puis envoyer le brouillon Forfy à l’assistant pour obtenir la version finale.`,
  };
}

function formatActionKitForCopy(kit: GeneratedActionKit) {
  return [
    'KIT FORFY COMMERCE',
    '',
    `Titre: ${kit.title}`,
    '',
    'Résumé:',
    kit.summary,
    '',
    'Checklist:',
    ...kit.checklist.map((item, index) => `${index + 1}. ${item}`),
    '',
    'Message équipe:',
    kit.teamMessage,
    '',
    'Prochaine étape:',
    kit.nextStep,
  ].join('\n');
}


function buildActionPlanMetrics(plan: ActionPlanItem[]) {
  return {
    total: plan.length,
    todo: plan.filter((item) => item.status === 'à faire').length,
    running: plan.filter((item) => item.status === 'en cours').length,
    done: plan.filter((item) => item.status === 'terminé').length,
  };
}

function buildSprintPlanForCopy(
  ctx: ResolvedCommerceContext,
  brief: CommerceBrief,
  plan: ActionPlanItem[],
  kit: GeneratedActionKit,
  action: CommerceAction,
) {
  const sourceItems = plan.length > 0 ? plan : [buildPlanItem(action, kit)];
  const metrics = buildActionPlanMetrics(plan);
  const normalized = sourceItems.slice(0, 7);
  const days = Array.from({ length: 7 }, (_item, index) => {
    const item = normalized[index % normalized.length];
    const day = index + 1;
    const focus = day === 1
      ? 'Cadrer la mission et confirmer les données manquantes.'
      : day === 2
        ? 'Préparer les textes, visuels ou messages nécessaires.'
        : day === 3
          ? 'Valider les contraintes opérationnelles avec l’équipe.'
          : day === 4
            ? 'Exécuter le suivi terrain ou la publication manuelle.'
            : day === 5
              ? 'Mesurer les réactions, objections ou réservations observées.'
              : day === 6
                ? 'Ajuster le message et prioriser les relances.'
                : 'Faire le bilan, décider la suite et archiver les apprentissages.';

    return `Jour ${day} — ${item.title}\n- Priorité: ${item.priority}\n- Statut départ: ${item.status}\n- Focus: ${focus}\n- Prochaine action: ${item.nextStep}`;
  });

  return [
    'SPRINT FORFY COMMERCE 7 JOURS',
    '',
    `Commerce: ${ctx.businessName}`,
    `Onglet actif: ${ctx.activeTabLabel}`,
    `Rôle: ${ctx.userRole}`,
    `Objectif brief: ${brief.objective}`,
    `Client cible: ${brief.targetClient || 'à préciser'}`,
    `Canal: ${brief.channel}`,
    `Ton: ${brief.tone}`,
    '',
    'TABLEAU DE BORD LOCAL',
    `- Total suivis: ${metrics.total}`,
    `- À faire: ${metrics.todo}`,
    `- En cours: ${metrics.running}`,
    `- Terminés: ${metrics.done}`,
    '',
    'SÉQUENCE 7 JOURS',
    ...days,
    '',
    'RITUEL QUOTIDIEN',
    '- Chaque matin: choisir 1 action prioritaire et confirmer les données manquantes.',
    '- Chaque après-midi: exécuter ou relancer manuellement.',
    '- Chaque soir: avancer le statut dans le plan local Forfy.',
    '',
    'GARDE-FOUS FINANCIERS',
    '- Ne modifier aucun paiement, prix officiel, remboursement, commission, payout, taxe ou référence Stripe.',
    '- Vérifier toute décision financière ou transactionnelle dans Giftshop avant action.',
  ].join('\n');
}


function buildCampaignPackForCopy(
  action: CommerceAction,
  ctx: ResolvedCommerceContext,
  brief: CommerceBrief,
  kit: GeneratedActionKit,
  sprint: string,
): GeneratedCampaignPack {
  const objective = brief.objective || 'objectif à préciser';
  const target = brief.targetClient || 'client cible à préciser';
  const channel = brief.channel || 'Forfeo';
  const tone = brief.tone || 'Premium et chaleureux';
  const sprintSignal = sprint.includes('SPRINT FORFY COMMERCE 7 JOURS')
    ? 'Sprint 7 jours déjà préparé'
    : 'Sprint à générer ou à confirmer';

  return {
    title: `Pack campagne — ${action.title}`,
    audience: target,
    promise: `Présenter ${ctx.businessName} avec un angle ${tone.toLowerCase()} pour ${objective.toLowerCase()}, sans inventer de prix, disponibilité ou avantage financier.`,
    channels: [
      {
        label: 'Facebook / Instagram',
        content: `Découvrez une expérience Forfeo pensée pour ${target}. ${kit.summary} Réservez ou contactez le commerce après validation des disponibilités.`,
      },
      {
        label: 'TikTok / Reels',
        content: `Plan vidéo court: 1) montrer l’ambiance, 2) présenter la valeur client, 3) finir avec un appel à réserver sur Forfeo après confirmation manuelle des détails.`,
      },
      {
        label: 'LinkedIn',
        content: `${ctx.businessName} structure une mission Forfeo autour de « ${action.title} ». Objectif: ${objective}. Approche: claire, professionnelle et validée avant diffusion.`,
      },
      {
        label: 'Courriel',
        content: `Objet: Une expérience Forfeo à découvrir. Message: Bonjour, nous préparons une expérience adaptée à ${target}. Voici l’idée: ${kit.teamMessage} Tous les détails importants seront validés avant confirmation.`,
      },
      {
        label: 'SMS / Messenger',
        content: `Bonjour! ${ctx.businessName} prépare une expérience Forfeo pour ${target}. Souhaitez-vous recevoir les détails une fois les disponibilités confirmées?`,
      },
    ],
    contentAngles: [
      `Angle principal: ${objective}.`,
      `Canal prioritaire du brief: ${channel}.`,
      `Ton demandé: ${tone}.`,
      `Signal sprint: ${sprintSignal}.`,
    ],
    manualChecks: [
      'Valider prix, taxes, disponibilités, politiques et conditions dans les systèmes autorisés.',
      'Confirmer que le message ne promet aucun remboursement, crédit, commission, payout ou rabais non approuvé.',
      'Relire le texte pour éviter toute promesse opérationnelle non confirmée.',
      'Publier manuellement ou via un outil autorisé seulement après validation humaine.',
    ],
  };
}

function formatCampaignPackForCopy(pack: GeneratedCampaignPack) {
  return [
    'PACK CAMPAGNE FORFY COMMERCE',
    '',
    `Titre: ${pack.title}`,
    `Audience: ${pack.audience}`,
    `Promesse: ${pack.promise}`,
    '',
    'CONTENUS PAR CANAL',
    ...pack.channels.map((channel) => `${channel.label}:\n${channel.content}`),
    '',
    'ANGLES DE CONTENU',
    ...pack.contentAngles.map((angle, index) => `${index + 1}. ${angle}`),
    '',
    'VALIDATIONS MANUELLES OBLIGATOIRES',
    ...pack.manualChecks.map((check, index) => `${index + 1}. ${check}`),
    '',
    'GARDE-FOUS FINANCIERS',
    '- Aucune publication automatique depuis ce cockpit.',
    '- Aucun prix, paiement, remboursement, commission, payout, taxe ou Stripe n’est modifié.',
    '- Giftshop demeure la source financière et transactionnelle.',
  ].join('\n');
}

export default function ForfyCommerceCockpit({
  context,
  onClose,
  onOpenTab,
  onSeedPrompt,
}: ForfyCommerceCockpitProps) {
  const resolved = resolveContext(context);
  const historyStorageKey = useMemo(() => buildHistoryStorageKey(resolved), [resolved.businessName, resolved.userRole]);
  const briefStorageKey = useMemo(() => buildBriefStorageKey(resolved), [resolved.businessName, resolved.userRole]);
  const planStorageKey = useMemo(() => buildPlanStorageKey(resolved), [resolved.businessName, resolved.userRole]);
  const recommendedAction = commerceActions.find((action) => action.tab === resolved.activeTab) || commerceActions[0];

  const [brief, setBrief] = useState<CommerceBrief>(defaultBrief);
  const [selectedActionId, setSelectedActionId] = useState(recommendedAction.id);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState(commercePlaybooks[0].id);
  const selectedPlaybook =
    commercePlaybooks.find((playbook) => playbook.id === selectedPlaybookId) ?? commercePlaybooks[0];
  const selectedAction = commerceActions.find((action) => action.id === selectedActionId) || recommendedAction;
  const [draftPrompt, setDraftPrompt] = useState(() => recommendedAction.buildPrompt(resolved, defaultBrief));
  const [draftStatus, setDraftStatus] = useState<'prêt' | 'préparé' | 'copié' | 'envoyé'>('prêt');
  const [history, setHistory] = useState<ActionHistoryEntry[]>([]);
  const [actionKit, setActionKit] = useState<GeneratedActionKit>(() => buildActionKit(recommendedAction, resolved, defaultBrief));
  const [kitStatus, setKitStatus] = useState<'prêt' | 'généré' | 'copié' | 'envoyé'>('prêt');
  const [actionPlan, setActionPlan] = useState<ActionPlanItem[]>([]);
  const [sprintText, setSprintText] = useState(() => buildSprintPlanForCopy(resolved, defaultBrief, [], buildActionKit(recommendedAction, resolved, defaultBrief), recommendedAction));
  const [sprintStatus, setSprintStatus] = useState<'prêt' | 'généré' | 'copié' | 'envoyé'>('prêt');
  const [campaignPack, setCampaignPack] = useState<GeneratedCampaignPack>(() => buildCampaignPackForCopy(recommendedAction, resolved, defaultBrief, buildActionKit(recommendedAction, resolved, defaultBrief), sprintText));
  const [campaignStatus, setCampaignStatus] = useState<'prêt' | 'généré' | 'copié' | 'envoyé'>('prêt');
  const planMetrics = useMemo(() => buildActionPlanMetrics(actionPlan), [actionPlan]);

  const homeIntelligenceSignals = useMemo(() => ([
    {
      id: 'mission',
      label: 'Mission active',
      value: selectedAction.title,
      helper: `Onglet: ${resolved.activeTabLabel}`,
      status: draftStatus === 'envoyé' ? 'actif' : 'prêt',
      target: 'forfy-missions',
      icon: <FaCompass />,
    },
    {
      id: 'brief',
      label: 'Brief marchand',
      value: brief.objective,
      helper: `${brief.channel} · ${brief.tone}`,
      status: brief.extraContext ? 'actif' : 'attention',
      target: 'forfy-brief',
      icon: <FaClipboardList />,
    },
    {
      id: 'kit',
      label: 'Kit prêt',
      value: kitStatus,
      helper: actionKit.title,
      status: kitStatus === 'prêt' ? 'attention' : 'actif',
      target: 'forfy-kit',
      icon: <FaGift />,
    },
    {
      id: 'plan',
      label: 'Plan local',
      value: `${planMetrics.total} suivi${planMetrics.total > 1 ? 's' : ''}`,
      helper: `${planMetrics.todo} à faire · ${planMetrics.running} en cours · ${planMetrics.done} terminés`,
      status: planMetrics.total > 0 ? 'actif' : 'vide',
      target: 'forfy-plan',
      icon: <FaClipboardList />,
    },
    {
      id: 'sprint',
      label: 'Sprint 7 jours',
      value: sprintStatus,
      helper: planMetrics.total > 0 ? 'Prêt à générer depuis le plan local' : 'Ajoute un kit au plan avant le sprint',
      status: sprintStatus === 'envoyé' || sprintStatus === 'généré' ? 'actif' : 'attention',
      target: 'forfy-sprint',
      icon: <FaRoute />,
    },
    {
      id: 'campaign',
      label: 'Campagne',
      value: campaignStatus,
      helper: 'Facebook, TikTok, LinkedIn, courriel, SMS',
      status: campaignStatus === 'envoyé' || campaignStatus === 'généré' ? 'actif' : 'attention',
      target: 'forfy-campaign',
      icon: <FaBullhorn />,
    },
    {
      id: 'history',
      label: 'Historique',
      value: `${history.length} action${history.length > 1 ? 's' : ''}`,
      helper: 'Mémoire locale du navigateur',
      status: history.length > 0 ? 'actif' : 'vide',
      target: 'forfy-history',
      icon: <FaHistory />,
    },
  ]), [
    actionKit.title,
    brief.channel,
    brief.extraContext,
    brief.objective,
    brief.tone,
    campaignStatus,
    draftStatus,
    history.length,
    kitStatus,
    planMetrics.done,
    planMetrics.running,
    planMetrics.todo,
    planMetrics.total,
    resolved.activeTabLabel,
    selectedAction.title,
    sprintStatus,
  ]);

  const homeNextStep = useMemo(() => {
    if (draftStatus === 'prêt') {
      return {
        title: 'Préparer la mission recommandée',
        description: `Commence par transformer « ${selectedAction.title} » en brouillon Forfy lisible et validable.`,
        target: 'forfy-draft',
      };
    }

    if (kitStatus === 'prêt') {
      return {
        title: 'Générer le kit prêt à utiliser',
        description: 'Transforme le brouillon en résumé, checklist, message équipe et prochaine étape.',
        target: 'forfy-kit',
      };
    }

    if (planMetrics.total === 0) {
      return {
        title: 'Ajouter le kit au plan local',
        description: 'Crée un vrai suivi marchand local avant de générer ton sprint 7 jours.',
        target: 'forfy-plan',
      };
    }

    if (sprintStatus === 'prêt') {
      return {
        title: 'Générer le sprint 7 jours',
        description: 'Transforme tes suivis locaux en séquence d’exécution claire pour la semaine.',
        target: 'forfy-sprint',
      };
    }

    if (campaignStatus === 'prêt') {
      return {
        title: 'Générer le pack campagne',
        description: 'Convertis le sprint en contenus Facebook, TikTok, LinkedIn, courriel et SMS.',
        target: 'forfy-campaign',
      };
    }

    return {
      title: 'Envoyer à l’assistant Forfy Commerce',
      description: 'Fais relire ton brouillon, ton kit ou ta campagne dans l’éditeur Forfy Commerce avant usage.',
      target: 'forfy-draft',
    };
  }, [campaignStatus, draftStatus, kitStatus, planMetrics.total, selectedAction.title, sprintStatus]);


  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(briefStorageKey);
      if (!raw) {
        setBrief(defaultBrief);
        return;
      }

      const parsed = JSON.parse(raw) as Partial<CommerceBrief>;
      setBrief({
        objective: parsed.objective || defaultBrief.objective,
        targetClient: parsed.targetClient || defaultBrief.targetClient,
        channel: parsed.channel || defaultBrief.channel,
        tone: parsed.tone || defaultBrief.tone,
        extraContext: parsed.extraContext || '',
      });
    } catch (_error) {
      setBrief(defaultBrief);
    }
  }, [briefStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(briefStorageKey, JSON.stringify(brief));
    } catch (_error) {
      // Local brief persistence is a convenience only. Never block the cockpit.
    }
  }, [brief, briefStorageKey]);

  useEffect(() => {
    setSelectedActionId(recommendedAction.id);
  }, [recommendedAction.id]);

  useEffect(() => {
    const action = commerceActions.find((item) => item.id === selectedActionId) || recommendedAction;
    setDraftPrompt(action.buildPrompt(resolved, brief));
    setDraftStatus('prêt');
  }, [
    brief.objective,
    brief.targetClient,
    brief.channel,
    brief.tone,
    brief.extraContext,
    recommendedAction,
    resolved.activeTab,
    resolved.activeTabLabel,
    resolved.businessName,
    resolved.businessPlan,
    resolved.userRole,
    selectedActionId,
  ]);


  useEffect(() => {
    const action = commerceActions.find((item) => item.id === selectedActionId) || recommendedAction;
    const nextKit = buildActionKit(action, resolved, brief);
    setActionKit(nextKit);
    setKitStatus('prêt');
    setSprintStatus('prêt');
    setCampaignPack(buildCampaignPackForCopy(action, resolved, brief, nextKit, sprintText));
    setCampaignStatus('prêt');
  }, [
    brief.objective,
    brief.targetClient,
    brief.channel,
    brief.tone,
    brief.extraContext,
    recommendedAction,
    resolved.activeTab,
    resolved.activeTabLabel,
    resolved.businessName,
    resolved.businessPlan,
    resolved.userRole,
    selectedActionId,
    sprintText,
  ]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(historyStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ActionHistoryEntry[];
      if (Array.isArray(parsed)) {
        setHistory(parsed.slice(0, 6));
      }
    } catch (_error) {
      setHistory([]);
    }
  }, [historyStorageKey]);


  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(planStorageKey);
      if (!raw) {
        setActionPlan([]);
        return;
      }
      const parsed = JSON.parse(raw) as ActionPlanItem[];
      if (Array.isArray(parsed)) {
        setActionPlan(parsed.slice(0, 8));
      }
    } catch (_error) {
      setActionPlan([]);
    }
  }, [planStorageKey]);

  const rememberAction = (action: CommerceAction, status: ActionHistoryEntry['status']) => {
    const entry: ActionHistoryEntry = {
      id: buildHistoryId(),
      actionId: action.id,
      title: action.title,
      status,
      createdAt: new Date().toISOString(),
    };

    setHistory((current) => {
      const next = [entry, ...current].slice(0, 6);
      try {
        window.localStorage.setItem(historyStorageKey, JSON.stringify(next));
      } catch (_error) {
        // Local history is a convenience only. Never block the cockpit.
      }
      return next;
    });
  };

  const saveActionPlan = (next: ActionPlanItem[]) => {
    try {
      window.localStorage.setItem(planStorageKey, JSON.stringify(next));
    } catch (_error) {
      // Local action plan is browser-only convenience. Never block Forfy.
    }
  };

  const addCurrentKitToPlan = () => {
    const entry = buildPlanItem(selectedAction, actionKit);
    setActionPlan((current) => {
      const next = [entry, ...current].slice(0, 8);
      saveActionPlan(next);
      return next;
    });
    rememberAction(selectedAction, 'préparé');
  };

  const advancePlanItem = (itemId: string) => {
    setActionPlan((current) => {
      const next = current.map((item) => (
        item.id === itemId ? { ...item, status: nextPlanStatus(item.status) } : item
      ));
      saveActionPlan(next);
      return next;
    });
  };

  const removePlanItem = (itemId: string) => {
    setActionPlan((current) => {
      const next = current.filter((item) => item.id !== itemId);
      saveActionPlan(next);
      return next;
    });
  };

  const clearCompletedPlanItems = () => {
    setActionPlan((current) => {
      const next = current.filter((item) => item.status !== 'terminé');
      saveActionPlan(next);
      return next;
    });
  };


  const generateSprintPlan = () => {
    const next = buildSprintPlanForCopy(resolved, brief, actionPlan, actionKit, selectedAction);
    setSprintText(next);
    setSprintStatus('généré');
    setCampaignPack(buildCampaignPackForCopy(selectedAction, resolved, brief, actionKit, next));
    setCampaignStatus('prêt');
  };

  const copySprintPlan = async () => {
    try {
      const next = sprintText || buildSprintPlanForCopy(resolved, brief, actionPlan, actionKit, selectedAction);
      await navigator.clipboard.writeText(next);
      setSprintText(next);
      setSprintStatus('copié');
      rememberAction(selectedAction, 'copié');
    } catch (_error) {
      setSprintStatus('prêt');
    }
  };

  const sendSprintToAssistant = () => {
    const next = sprintText || buildSprintPlanForCopy(resolved, brief, actionPlan, actionKit, selectedAction);
    setSprintText(next);
    onSeedPrompt(`${draftPrompt}\n\n${next}`);
    setSprintStatus('envoyé');
    rememberAction(selectedAction, 'envoyé');
  };


  const generateCampaignPack = () => {
    const next = buildCampaignPackForCopy(selectedAction, resolved, brief, actionKit, sprintText);
    setCampaignPack(next);
    setCampaignStatus('généré');
  };

  const copyCampaignPack = async () => {
    try {
      const next = campaignPack || buildCampaignPackForCopy(selectedAction, resolved, brief, actionKit, sprintText);
      await navigator.clipboard.writeText(formatCampaignPackForCopy(next));
      setCampaignPack(next);
      setCampaignStatus('copié');
      rememberAction(selectedAction, 'copié');
    } catch (_error) {
      setCampaignStatus('prêt');
    }
  };

  const sendCampaignPackToAssistant = () => {
    const next = campaignPack || buildCampaignPackForCopy(selectedAction, resolved, brief, actionKit, sprintText);
    setCampaignPack(next);
    onSeedPrompt(`${draftPrompt}\n\n${formatActionKitForCopy(actionKit)}\n\n${sprintText}\n\n${formatCampaignPackForCopy(next)}`);
    setCampaignStatus('envoyé');
    rememberAction(selectedAction, 'envoyé');
  };

  const updateBrief = (patch: Partial<CommerceBrief>) => {
    setBrief((current) => ({ ...current, ...patch }));
    setDraftStatus('prêt');
    setKitStatus('prêt');
    setSprintStatus('prêt');
    setCampaignStatus('prêt');
  };

  const resetBrief = () => {
    setBrief(defaultBrief);
    setDraftStatus('prêt');
    setKitStatus('prêt');
    setSprintStatus('prêt');
    setCampaignStatus('prêt');
  };

  const refreshDraftFromBrief = () => {
    setDraftPrompt(selectedAction.buildPrompt(resolved, brief));
    setActionKit(buildActionKit(selectedAction, resolved, brief));
    setDraftStatus('préparé');
    setKitStatus('généré');
    setSprintStatus('prêt');
    setCampaignStatus('prêt');
    rememberAction(selectedAction, 'préparé');
  };

  const prepareAction = (action: CommerceAction) => {
    const prompt = action.buildPrompt(resolved, brief);
    setSelectedActionId(action.id);
    setDraftPrompt(prompt);
    setActionKit(buildActionKit(action, resolved, brief));
    setDraftStatus('préparé');
    setKitStatus('généré');
    setSprintStatus('prêt');
    setCampaignStatus('prêt');
    rememberAction(action, 'préparé');
  };

  const applyPlaybook = (playbook: CommercePlaybook) => {
    const action = commerceActions.find((item) => item.id === playbook.actionId) || selectedAction;
    const nextBrief: CommerceBrief = {
      objective: playbook.objective,
      targetClient: playbook.targetClient,
      channel: playbook.channel,
      tone: playbook.tone,
      extraContext: playbook.notes,
    };
    const prompt = `${action.buildPrompt(resolved, nextBrief)}\n\nPLAYBOOK FORFY COMMERCE\n- Playbook: ${playbook.title}\n- Catégorie: ${playbook.category}\n- Description: ${playbook.description}\n- Checklist terrain:\n${playbook.checklist.map((item, index) => `${index + 1}. ${item}`).join('\n')}\n\nRappel: validation humaine requise avant toute publication, offre, promesse opérationnelle ou action commerciale.`;
    const nextKit = buildActionKit(action, resolved, nextBrief);

    setSelectedPlaybookId(playbook.id);
    setSelectedActionId(action.id);
    setBrief(nextBrief);
    setDraftPrompt(prompt);
    setActionKit(nextKit);
    setDraftStatus('préparé');
    setKitStatus('généré');
    setSprintStatus('prêt');
    setCampaignPack(buildCampaignPackForCopy(action, resolved, nextBrief, nextKit, sprintText));
    setCampaignStatus('prêt');
    rememberAction(action, 'préparé');
    scrollToWorkspaceSection('forfy-draft');
  };

  const prepareSelectedPlaybook = () => applyPlaybook(selectedPlaybook);

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draftPrompt);
      setDraftStatus('copié');
      rememberAction(selectedAction, 'copié');
    } catch (_error) {
      setDraftStatus('prêt');
    }
  };

  const generateActionKit = () => {
    setActionKit(buildActionKit(selectedAction, resolved, brief));
    setKitStatus('généré');
    setSprintStatus('prêt');
    setCampaignStatus('prêt');
  };

  const copyActionKit = async () => {
    try {
      await navigator.clipboard.writeText(formatActionKitForCopy(actionKit));
      setKitStatus('copié');
      rememberAction(selectedAction, 'copié');
    } catch (_error) {
      setKitStatus('prêt');
    setSprintStatus('prêt');
    }
  };

  const sendKitToAssistant = () => {
    onSeedPrompt(`${draftPrompt}\n\n${formatActionKitForCopy(actionKit)}`);
    setKitStatus('envoyé');
    rememberAction(selectedAction, 'envoyé');
  };

  const sendDraftToAssistant = () => {
    onSeedPrompt(draftPrompt);
    setDraftStatus('envoyé');
    rememberAction(selectedAction, 'envoyé');
  };


  const scrollToWorkspaceSection = (sectionId: string) => {
    if (typeof document === 'undefined') return;
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="forfy-commerce-cockpit forfy-commerce-cockpit--v22d forfy-commerce-cockpit--v22e forfy-commerce-cockpit--v22f forfy-commerce-cockpit--v22g forfy-commerce-cockpit--v22h forfy-commerce-cockpit--v22i forfy-commerce-cockpit--v22j forfy-commerce-cockpit--v22k forfy-commerce-cockpit--v22k1 forfy-commerce-cockpit--v22k2 forfy-commerce-cockpit--v22k3 forfy-commerce-cockpit--v22l forfy-commerce-cockpit--v22m forfy-commerce-premium forfy-commerce-home-intelligence-lock forfy-commerce-playbook-library-lock" aria-label="Forfy Commerce Premium Assistant">
      <header className="forfy-commerce-cockpit__hero forfy-commerce-cockpit__hero--premium">
        <div className="forfy-commerce-cockpit__identity forfy-commerce-cockpit__identity--premium">
          <span className="forfy-commerce-cockpit__avatar forfy-commerce-cockpit__avatar--premium"><FaRobot /></span>
          <div>
            <p className="forfy-commerce-cockpit__eyebrow">Forfy Commerce ✨</p>
            <h2>Le copilote intelligent du commerçant</h2>
            <p>
              Plus de clients. Plus d’expériences. Plus de résultats — avec {resolved.businessName} au centre.
            </p>
          </div>
        </div>
        <div className="forfy-commerce-cockpit__hero-note" aria-label="Promesse Forfy Commerce">
          <strong>Des idées aux résultats.</strong>
          <span>Votre commerce, notre intelligence.</span>
        </div>
        <button type="button" className="forfy-commerce-cockpit__close forfy-commerce-cockpit__close--premium" onClick={onClose} aria-label="Fermer Forfy Commerce">
          <FaTimes />
        </button>
      </header>

      <div className="forfy-commerce-cockpit__mode-switch" aria-label="Mode Forfy">
        <button type="button" className="forfy-commerce-cockpit__mode-option" onClick={() => onSeedPrompt('J’ai besoin d’aide avec le support Forfeo Commerce. Garde le contexte marchand visible, mais réponds comme assistant support.') }>
          <span><FaComments /></span>
          <strong>Support</strong>
          <small>Aide et assistance</small>
        </button>
        <button type="button" className="forfy-commerce-cockpit__mode-option forfy-commerce-cockpit__mode-option--active">
          <span><FaChartLine /></span>
          <strong>Commerce</strong>
          <small>Stratégie et croissance</small>
        </button>
      </div>

      <div className="forfy-commerce-cockpit__workspace">
        <aside className="forfy-commerce-cockpit__sidebar" aria-label="Navigation interne Forfy Commerce">
          <nav className="forfy-commerce-cockpit__workspace-nav">
            {workspaceNav.map((item) => (
              <button key={item.id} type="button" onClick={() => scrollToWorkspaceSection(item.id)}>
                <span>{item.icon}</span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.helper}</small>
                </div>
              </button>
            ))}
          </nav>

          <article className="forfy-commerce-cockpit__brand-card">
            <p>Propulsé par</p>
            <h3>Forfeo</h3>
            <span>Le tourisme local va plus loin avec l’IA.</span>
          </article>
        </aside>

        <main className="forfy-commerce-cockpit__main" aria-label="Espace de travail Forfy Commerce">
          <section id="forfy-home" className="forfy-commerce-cockpit__home forfy-commerce-cockpit__section-card" aria-label="Accueil Forfy Commerce">
            <div className="forfy-commerce-cockpit__welcome">
              <p className="forfy-commerce-cockpit__card-eyebrow">Bonjour 👋</p>
              <h3>Que souhaites-tu accomplir aujourd’hui?</h3>
              <p>Forfy t’aide à attirer plus de clients, à mieux vendre et à faire rayonner tes expériences.</p>
            </div>
            <button type="button" className="forfy-commerce-cockpit__primary forfy-commerce-cockpit__new-mission" onClick={() => prepareAction(recommendedAction)}>
              <FaMagic /> Nouvelle mission
            </button>

            <div className="forfy-commerce-cockpit__home-intelligence forfy-commerce-cockpit__home-intelligence--v22l" data-forfy-home-intelligence="v22l">
              <article className="forfy-commerce-cockpit__home-next" aria-label="Prochaine étape intelligente Forfy Commerce">
                <p className="forfy-commerce-cockpit__card-eyebrow">Accueil intelligent</p>
                <h4>{homeNextStep.title}</h4>
                <p>{homeNextStep.description}</p>
                <div className="forfy-commerce-cockpit__home-next-actions">
                  <button type="button" className="forfy-commerce-cockpit__primary" onClick={() => prepareAction(selectedAction)}>
                    <FaMagic /> Préparer maintenant
                  </button>
                  <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => scrollToWorkspaceSection(homeNextStep.target)}>
                    Voir la section <FaArrowRight />
                  </button>
                </div>
              </article>

              <div className="forfy-commerce-cockpit__home-signals" aria-label="Signaux locaux Forfy Commerce">
                {homeIntelligenceSignals.map((signal) => (
                  <button
                    key={signal.id}
                    type="button"
                    className={`forfy-commerce-cockpit__home-signal forfy-commerce-cockpit__home-signal--${signal.status}`}
                    onClick={() => scrollToWorkspaceSection(signal.target)}
                  >
                    <span>{signal.icon}</span>
                    <strong>{signal.label}</strong>
                    <em>{signal.value}</em>
                    <small>{signal.helper}</small>
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="forfy-commerce-cockpit__dashboard-grid" aria-label="Tableau de bord Forfy Commerce">
            <article className="forfy-commerce-cockpit__next-best forfy-commerce-cockpit__section-card">
              <p className="forfy-commerce-cockpit__card-eyebrow">Prochaine meilleure action</p>
              <h3>{recommendedAction.title}</h3>
              <p>{recommendedAction.description}</p>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={() => prepareAction(recommendedAction)}>
                Préparer maintenant <FaArrowRight />
              </button>
            </article>

            <article className="forfy-commerce-cockpit__context forfy-commerce-cockpit__context-panel forfy-commerce-cockpit__section-card" data-forfy-commerce-context="true">
              <div className="forfy-commerce-cockpit__panel-title">
                <span className="forfy-commerce-cockpit__execution-icon"><FaStore /></span>
                <div>
                  <p className="forfy-commerce-cockpit__card-eyebrow">Contexte marchand</p>
                  <h3>{resolved.businessName}</h3>
                </div>
              </div>
              <dl className="forfy-commerce-cockpit__context-list">
                <div><dt>Onglet actif</dt><dd>{resolved.activeTabLabel}</dd></div>
                <div><dt>Rôle</dt><dd>{resolved.userRole}</dd></div>
                <div><dt>Plan</dt><dd>{resolved.businessPlan}</dd></div>
              </dl>
              <div className="forfy-commerce-cockpit__guardrail">
                <FaLock />
                <p>
                  Forfy prépare, résume et suggère. Il ne modifie aucun paiement, prix officiel, remboursement,
                  commission, payout, taxe ou référence Stripe. Giftshop demeure l’autorité financière.
                </p>
              </div>
            </article>

            <article className="forfy-commerce-cockpit__brief-snapshot forfy-commerce-cockpit__section-card">
              <p className="forfy-commerce-cockpit__card-eyebrow">Brief marchand</p>
              <h3>{brief.objective}</h3>
              <ul>
                <li><strong>Client</strong><span>{brief.targetClient}</span></li>
                <li><strong>Canal</strong><span>{brief.channel}</span></li>
                <li><strong>Ton</strong><span>{brief.tone}</span></li>
              </ul>
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => scrollToWorkspaceSection('forfy-brief')}>Modifier le brief</button>
            </article>

            <article className="forfy-commerce-cockpit__mini-actions forfy-commerce-cockpit__section-card">
              <p className="forfy-commerce-cockpit__card-eyebrow">Actions intelligentes</p>
              <h3>Préparer, copier, envoyer</h3>
              {commerceActions.slice(0, 4).map((action) => (
                <div key={action.id}>
                  <span>{action.icon}</span>
                  <strong>{action.title}</strong>
                  <button type="button" onClick={() => prepareAction(action)}>Préparer</button>
                </div>
              ))}
            </article>
          </section>

          <div className="forfy-commerce-cockpit__playbook" aria-label="Méthode Forfy Commerce">
            {playbookSteps.map((step, index) => (
              <span key={step}><strong>{index + 1}</strong>{step}</span>
            ))}
          </div>

          <div className="forfy-commerce-cockpit__tabs" aria-label="Raccourcis Commerce">
            {quickTabs.map((tab) => (
              <button key={tab.id} type="button" onClick={() => onOpenTab(tab.id)}>{tab.label}</button>
            ))}
          </div>

          <section id="forfy-missions" className="forfy-commerce-cockpit__spotlight forfy-commerce-cockpit__section-card" aria-label="Mission recommandée">
            <span className="forfy-commerce-cockpit__spotlight-icon"><FaMagic /></span>
            <div>
              <p className="forfy-commerce-cockpit__card-eyebrow">Mission recommandée</p>
              <h3>{recommendedAction.title}</h3>
              <p>
                Forfy peut préparer un brouillon contextualisé selon l’onglet actif: {resolved.activeTabLabel}.
              </p>
            </div>
            <button type="button" className="forfy-commerce-cockpit__primary" onClick={() => prepareAction(recommendedAction)}>
              Préparer <FaArrowRight />
            </button>
          </section>

          <section id="forfy-playbooks" className="forfy-commerce-cockpit__playbook-library forfy-commerce-cockpit__section-card" data-forfy-playbooks="v22m" aria-label="Bibliothèque de playbooks Forfy Commerce">
            <div className="forfy-commerce-cockpit__playbook-library-head">
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Bibliothèque de playbooks</p>
                <h3>Choisir une mission prête à lancer</h3>
                <p>Forfy applique le modèle au brief, prépare le brouillon et garde la validation humaine obligatoire.</p>
              </div>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={prepareSelectedPlaybook}>
                Utiliser le playbook sélectionné <FaArrowRight />
              </button>
            </div>

            <div className="forfy-commerce-cockpit__playbook-library-grid">
              {commercePlaybooks.map((playbook) => {
                const action = commerceActions.find((item) => item.id === playbook.actionId) || selectedAction;
                const isSelected = selectedPlaybook.id === playbook.id;

                return (
                  <article key={playbook.id} className={`forfy-commerce-cockpit__playbook-card ${isSelected ? 'forfy-commerce-cockpit__playbook-card--active' : ''}`}>
                    <div>
                      <span>{playbook.category}</span>
                      <h4>{playbook.title}</h4>
                      <p>{playbook.description}</p>
                    </div>
                    <ul>
                      {playbook.checklist.slice(0, 3).map((item) => <li key={item}>{item}</li>)}
                    </ul>
                    <footer>
                      <small>{action.title} · {tabLabels[playbook.tab] || playbook.tab}</small>
                      <div>
                        <button type="button" onClick={() => setSelectedPlaybookId(playbook.id)}>Sélectionner</button>
                        <button type="button" onClick={() => applyPlaybook(playbook)}>Préparer</button>
                      </div>
                    </footer>
                  </article>
                );
              })}
            </div>

            <article className="forfy-commerce-cockpit__playbook-selected" aria-label="Playbook sélectionné">
              <p className="forfy-commerce-cockpit__card-eyebrow">Playbook actif</p>
              <h4>{selectedPlaybook.title}</h4>
              <p>{selectedPlaybook.notes}</p>
              <div>
                <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => onOpenTab(selectedPlaybook.tab)}>
                  Ouvrir l’onglet recommandé
                </button>
                <button type="button" className="forfy-commerce-cockpit__primary" onClick={prepareSelectedPlaybook}>
                  Préparer ce playbook <FaMagic />
                </button>
              </div>
            </article>
          </section>

          <section id="forfy-brief" className="forfy-commerce-cockpit__brief forfy-commerce-cockpit__brief--v22f forfy-commerce-cockpit__section-card" aria-label="Brief marchand intelligent">
            <div className="forfy-commerce-cockpit__brief-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaClipboardList /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Brief intelligent</p>
                <h3>Personnaliser la mission avant de préparer</h3>
                <p>Ce brief enrichit chaque prompt Forfy avec un objectif, une cible, un canal, un ton et tes notes terrain.</p>
              </div>
            </div>

            <div className="forfy-commerce-cockpit__brief-grid">
              <label>
                <span>Objectif</span>
                <select value={brief.objective} onChange={(event) => updateBrief({ objective: event.target.value })}>
                  {objectiveOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label>
                <span>Canal</span>
                <select value={brief.channel} onChange={(event) => updateBrief({ channel: event.target.value })}>
                  {channelOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label>
                <span>Ton</span>
                <select value={brief.tone} onChange={(event) => updateBrief({ tone: event.target.value })}>
                  {toneOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
            </div>

            <label className="forfy-commerce-cockpit__brief-target">
              <span>Client cible</span>
              <input
                value={brief.targetClient}
                onChange={(event) => updateBrief({ targetClient: event.target.value })}
                placeholder="Ex.: couples de Québec, familles, touristes, clients corporatifs..."
              />
            </label>

            <label className="forfy-commerce-cockpit__brief-notes">
              <span>Notes terrain</span>
              <textarea
                value={brief.extraContext}
                onChange={(event) => updateBrief({ extraContext: event.target.value })}
                placeholder="Ajoute les détails importants: saison, capacité, disponibilité, ambiance, restrictions, objectif commercial..."
              />
            </label>

            <div className="forfy-commerce-cockpit__brief-actions">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={resetBrief}>Réinitialiser</button>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={refreshDraftFromBrief}>Actualiser le brouillon</button>
            </div>
          </section>

                    <ForfyCommerceOfferAssistant
            onOpenOffers={() => onOpenTab('offersHub' as any)}
            onSendToAssistant={onSeedPrompt}
          />
<section id="forfy-draft" className="forfy-commerce-cockpit__execution forfy-commerce-cockpit__execution--v22e forfy-commerce-cockpit__section-card" aria-label="Brouillon Forfy Commerce">
            <div className="forfy-commerce-cockpit__execution-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaClipboardList /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Brouillon Forfy</p>
                <h3>{selectedAction.title}</h3>
                <p>{selectedAction.description}</p>
              </div>
              <span className={`forfy-commerce-cockpit__status forfy-commerce-cockpit__status--${draftStatus}`}>{draftStatus}</span>
            </div>

            <textarea
              className="forfy-commerce-cockpit__draft"
              value={draftPrompt}
              onChange={(event) => {
                setDraftPrompt(event.target.value);
                setDraftStatus('prêt');
              }}
              aria-label="Prompt préparé par Forfy Commerce"
            />

            <div className="forfy-commerce-cockpit__execution-actions">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={copyDraft}>
                <FaCopy /> Copier
              </button>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={sendDraftToAssistant}>
                <FaPaperPlane /> Envoyer à l’assistant
              </button>
            </div>
          </section>

          <section id="forfy-kit" className="forfy-commerce-cockpit__kit forfy-commerce-cockpit__kit--v22g forfy-commerce-cockpit__section-card" aria-label="Kit Forfy Commerce prêt à utiliser">
            <div className="forfy-commerce-cockpit__kit-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaRoute /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Kit prêt à utiliser</p>
                <h3>{actionKit.title}</h3>
                <p>Forfy transforme la mission choisie en résumé, checklist, message équipe et prochaine étape.</p>
              </div>
              <span className={`forfy-commerce-cockpit__status forfy-commerce-cockpit__status--${kitStatus}`}>{kitStatus}</span>
            </div>

            <div className="forfy-commerce-cockpit__kit-summary">
              <p>{actionKit.summary}</p>
            </div>

            <div className="forfy-commerce-cockpit__kit-grid">
              <article>
                <p className="forfy-commerce-cockpit__card-eyebrow">Checklist</p>
                <ul>
                  {actionKit.checklist.map((item) => <li key={item}><FaCheckCircle />{item}</li>)}
                </ul>
              </article>
              <article>
                <p className="forfy-commerce-cockpit__card-eyebrow">Message équipe</p>
                <p>{actionKit.teamMessage}</p>
                <p className="forfy-commerce-cockpit__kit-next"><strong>Prochaine étape:</strong> {actionKit.nextStep}</p>
              </article>
            </div>

            <div className="forfy-commerce-cockpit__execution-actions">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={generateActionKit}>
                <FaMagic /> Générer le kit
              </button>
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={addCurrentKitToPlan}>
                <FaClipboardList /> Ajouter au plan local
              </button>
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={copyActionKit}>
                <FaCopy /> Copier le kit
              </button>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={sendKitToAssistant}>
                <FaPaperPlane /> Envoyer le kit à l’assistant
              </button>
            </div>
          </section>

          <div className="forfy-commerce-cockpit__actions forfy-commerce-cockpit__actions--v22d forfy-commerce-cockpit__actions--v22e forfy-commerce-cockpit__section-card" aria-label="Missions intelligentes Forfy Commerce">
            {commerceActions.map((action) => (
              <article key={action.id} className={`forfy-commerce-cockpit__card ${action.id === selectedAction.id ? 'forfy-commerce-cockpit__card--selected' : ''}`}>
                <span className="forfy-commerce-cockpit__card-icon">{action.icon}</span>
                <div>
                  <p className="forfy-commerce-cockpit__card-eyebrow">{action.eyebrow}</p>
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                  <ul className="forfy-commerce-cockpit__outputs" aria-label={`Sortie attendue pour ${action.title}`}>
                    <li><FaCheckCircle />{action.output}</li>
                  </ul>
                </div>
                <div className="forfy-commerce-cockpit__card-actions">
                  <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => onOpenTab(action.tab)}>
                    <FaCompass /> Ouvrir
                  </button>
                  <button type="button" className="forfy-commerce-cockpit__primary" onClick={() => prepareAction(action)}>
                    <FaCopy /> Préparer avec brief
                  </button>
                </div>
              </article>
            ))}
          </div>

          <section id="forfy-plan" className="forfy-commerce-cockpit__plan forfy-commerce-cockpit__plan--v22h forfy-commerce-cockpit__section-card" aria-label="Plan d’exécution local Forfy Commerce">
            <div className="forfy-commerce-cockpit__plan-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaClipboardList /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Plan d’exécution local</p>
                <h3>Transformer les kits Forfy en suivis concrets</h3>
                <p>Ajoute un kit au plan, fais avancer son statut et garde une liste simple côté navigateur.</p>
              </div>
              {actionPlan.some((item) => item.status === 'terminé') ? (
                <button type="button" className="forfy-commerce-cockpit__secondary" onClick={clearCompletedPlanItems}>Nettoyer terminés</button>
              ) : null}
            </div>

            {actionPlan.length > 0 ? (
              <ul className="forfy-commerce-cockpit__plan-list">
                {actionPlan.map((item) => (
                  <li key={item.id} className={`forfy-commerce-cockpit__plan-item forfy-commerce-cockpit__plan-item--${planStatusSlug(item.status)}`}>
                    <div>
                      <p className="forfy-commerce-cockpit__card-eyebrow">Priorité {item.priority}</p>
                      <h4>{item.title}</h4>
                      <p>{item.nextStep}</p>
                      <small>{formatHistoryTime(item.createdAt)}</small>
                    </div>
                    <div className="forfy-commerce-cockpit__plan-controls">
                      <span className={`forfy-commerce-cockpit__plan-status forfy-commerce-cockpit__plan-status--${planStatusSlug(item.status)}`}>{item.status}</span>
                      <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => advancePlanItem(item.id)}>
                        <FaBolt /> Avancer
                      </button>
                      <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => removePlanItem(item.id)} aria-label={`Retirer ${item.title} du plan local`}>
                        <FaTrash /> Retirer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="forfy-commerce-cockpit__plan-empty">Aucun suivi local. Génère un kit, puis clique “Ajouter au plan local”.</p>
            )}
          </section>

          <section id="forfy-sprint" className="forfy-commerce-cockpit__sprint forfy-commerce-cockpit__sprint--v22i forfy-commerce-cockpit__section-card" aria-label="Sprint marchand 7 jours Forfy Commerce">
            <div className="forfy-commerce-cockpit__sprint-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaRoute /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Sprint marchand 7 jours</p>
                <h3>Transformer le plan local en exécution terrain</h3>
                <p>Forfy assemble tes suivis locaux en séquence hebdomadaire copiable, modifiable et envoyable à l’assistant.</p>
              </div>
              <span className={`forfy-commerce-cockpit__status forfy-commerce-cockpit__status--${sprintStatus}`}>{sprintStatus}</span>
            </div>

            <div className="forfy-commerce-cockpit__sprint-metrics" aria-label="Résumé du plan local">
              <span><strong>{planMetrics.total}</strong> suivis</span>
              <span><strong>{planMetrics.todo}</strong> à faire</span>
              <span><strong>{planMetrics.running}</strong> en cours</span>
              <span><strong>{planMetrics.done}</strong> terminés</span>
            </div>

            <textarea
              className="forfy-commerce-cockpit__sprint-draft"
              value={sprintText}
              onChange={(event) => {
                setSprintText(event.target.value);
                setSprintStatus('prêt');
              }}
              aria-label="Sprint Forfy Commerce 7 jours"
            />

            <div className="forfy-commerce-cockpit__execution-actions">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={generateSprintPlan}>
                <FaMagic /> Générer sprint 7 jours
              </button>
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={copySprintPlan}>
                <FaCopy /> Copier sprint
              </button>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={sendSprintToAssistant}>
                <FaPaperPlane /> Envoyer sprint à l’assistant
              </button>
            </div>
          </section>

          <section id="forfy-campaign" className="forfy-commerce-cockpit__campaign forfy-commerce-cockpit__campaign--v22j forfy-commerce-cockpit__section-card" aria-label="Pack campagne Forfy Commerce">
            <div className="forfy-commerce-cockpit__campaign-head">
              <span className="forfy-commerce-cockpit__execution-icon"><FaBullhorn /></span>
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Pack campagne multicanal</p>
                <h3>Transformer le sprint en contenus prêts à valider</h3>
                <p>Forfy prépare des variantes Facebook, TikTok, LinkedIn, courriel et SMS sans publication automatique.</p>
              </div>
              <span className={`forfy-commerce-cockpit__status forfy-commerce-cockpit__status--${campaignStatus}`}>{campaignStatus}</span>
            </div>

            <div className="forfy-commerce-cockpit__campaign-summary">
              <p><strong>{campaignPack.title}</strong></p>
              <p>{campaignPack.promise}</p>
            </div>

            <div className="forfy-commerce-cockpit__campaign-grid">
              {campaignPack.channels.map((channel) => (
                <article key={channel.label}>
                  <p className="forfy-commerce-cockpit__card-eyebrow">{channel.label}</p>
                  <p>{channel.content}</p>
                </article>
              ))}
            </div>

            <div className="forfy-commerce-cockpit__campaign-checks">
              <p className="forfy-commerce-cockpit__card-eyebrow">Validations humaines</p>
              <ul>
                {campaignPack.manualChecks.map((check) => <li key={check}><FaCheckCircle />{check}</li>)}
              </ul>
            </div>

            <div className="forfy-commerce-cockpit__execution-actions">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={generateCampaignPack}>
                <FaMagic /> Générer pack campagne
              </button>
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={copyCampaignPack}>
                <FaCopy /> Copier campagne
              </button>
              <button type="button" className="forfy-commerce-cockpit__primary" onClick={sendCampaignPackToAssistant}>
                <FaPaperPlane /> Envoyer campagne à l’assistant
              </button>
            </div>
          </section>

          <section id="forfy-history" className="forfy-commerce-cockpit__history forfy-commerce-cockpit__section-card" aria-label="Historique local Forfy Commerce">
            <div className="forfy-commerce-cockpit__history-head">
              <FaHistory />
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Historique local</p>
                <h3>Dernières actions Forfy Commerce</h3>
              </div>
            </div>
            {history.length > 0 ? (
              <ul>
                {history.map((entry) => (
                  <li key={entry.id}>
                    <span>{entry.title}</span>
                    <strong>{entry.status}</strong>
                    <small>{formatHistoryTime(entry.createdAt)}</small>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="forfy-commerce-cockpit__history-empty">Aucune action locale pour l’instant. Prépare un brouillon pour démarrer.</p>
            )}
          </section>

          <footer className="forfy-commerce-cockpit__footer">
            <FaInfoCircle />
            <span>
              V2.2K redessine Forfy Commerce en assistant premium: navigation interne, accueil intelligent, cartes SaaS modernes et séparation claire Commerce / Support. Les publications, prix, paiements et décisions finales restent manuels ou côté système autorisé.
            </span>
          </footer>
        </main>
      </div>
    </section>
  );
}
